// Lightweight API client for curio. Token is sent on every call.

const API_BASE = '/api';

function getToken(): string | null {
  return localStorage.getItem('curio_token');
}

function setToken(t: string) {
  localStorage.setItem('curio_token', t);
}

function headers(extra: Record<string, string> = {}) {
  const token = getToken();
  return {
    'content-type': 'application/json',
    ...(token ? { 'X-User-Token': token } : {}),
    ...extra,
  };
}

async function j<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function bootstrap(existingToken?: string) {
  const res = await fetch(`${API_BASE}/bootstrap`, {
    method: 'POST',
    headers: headers(),
    body: existingToken ? JSON.stringify({ token: existingToken }) : undefined,
  });
  const data = await j<{ token: string; isNew?: boolean; restored?: boolean }>(res);
  setToken(data.token);
  return data;
}

export async function getMe() {
  const res = await fetch(`${API_BASE}/me`, { headers: headers() });
  return j<any>(res);
}

export async function updateEmail(email: string) {
  const res = await fetch(`${API_BASE}/me`, {
    method: 'PATCH',
    headers: headers(),
    body: JSON.stringify({ email }),
  });
  return j<any>(res);
}

export async function listThreads(opts?: { status?: string; taxon_id?: string }) {
  const qs = new URLSearchParams();
  if (opts?.status) qs.set('status', opts.status);
  if (opts?.taxon_id) qs.set('taxon_id', opts.taxon_id);
  const res = await fetch(`${API_BASE}/threads?${qs}`, { headers: headers() });
  return j<{ threads: any[] }>(res);
}

export async function createThread(initial_text: string, status = 'open') {
  const res = await fetch(`${API_BASE}/threads`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ initial_text, status }),
  });
  return j<{ thread: any }>(res);
}

export async function getThread(id: string) {
  const res = await fetch(`${API_BASE}/threads/${id}`, { headers: headers() });
  return j<any>(res);
}

export async function updateThread(id: string, patch: { status?: string; primary_taxon_id?: string | null }) {
  const res = await fetch(`${API_BASE}/threads/${id}`, {
    method: 'PATCH',
    headers: headers(),
    body: JSON.stringify(patch),
  });
  return j<any>(res);
}

export async function addReflection(thread_id: string, content: string, moves?: any) {
  const res = await fetch(`${API_BASE}/threads/${thread_id}/reflect`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ content, moves }),
  });
  return j<any>(res);
}

export async function assignThread(thread_id: string, taxon_id: string, note?: string) {
  const res = await fetch(`${API_BASE}/threads/${thread_id}/assign`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ taxon_id, note }),
  });
  return j<any>(res);
}

export async function unassignThread(thread_id: string, taxon_id: string) {
  const res = await fetch(`${API_BASE}/threads/${thread_id}/unassign`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ taxon_id }),
  });
  return j<any>(res);
}

export async function listTaxa() {
  const res = await fetch(`${API_BASE}/taxa`, { headers: headers() });
  return j<{ taxa: any[] }>(res);
}

export async function createTaxon(name: string, description?: string) {
  const res = await fetch(`${API_BASE}/taxa`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ name, description }),
  });
  return j<any>(res);
}

export async function getTaxon(id: string) {
  const res = await fetch(`${API_BASE}/taxa/${id}`, { headers: headers() });
  return j<any>(res);
}

export async function updateTaxon(id: string, patch: { name?: string; description?: string }) {
  const res = await fetch(`${API_BASE}/taxa/${id}`, {
    method: 'PATCH',
    headers: headers(),
    body: JSON.stringify(patch),
  });
  return j<any>(res);
}

export async function listScripts(taxon_id?: string) {
  const qs = taxon_id ? `?taxon_id=${taxon_id}` : '';
  const res = await fetch(`${API_BASE}/scripts${qs}`, { headers: headers() });
  return j<{ scripts: any[] }>(res);
}

export async function createScript(title: string, body: string, taxon_id?: string) {
  const res = await fetch(`${API_BASE}/scripts`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ title, body, taxon_id }),
  });
  return j<any>(res);
}

export async function useScript(script_id: string, thread_id: string, outcome_note?: string) {
  const res = await fetch(`${API_BASE}/scripts/${script_id}/use`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ thread_id, outcome_note }),
  });
  return j<any>(res);
}

export async function getLoom(params?: { event_type?: string; taxon_id?: string; limit?: number }) {
  const qs = new URLSearchParams();
  if (params?.event_type) qs.set('event_type', params.event_type);
  if (params?.taxon_id) qs.set('taxon_id', params.taxon_id);
  if (params?.limit) qs.set('limit', String(params.limit));
  const res = await fetch(`${API_BASE}/loom?${qs}`, { headers: headers() });
  return j<{ events: any[] }>(res);
}

export async function getLoomPatterns() {
  const res = await fetch(`${API_BASE}/loom/patterns`, { headers: headers() });
  return j<any>(res);
}

export async function requestExport() {
  const res = await fetch(`${API_BASE}/export`, {
    method: 'POST',
    headers: headers(),
  });
  return j<any>(res);
}

export async function listExports() {
  const res = await fetch(`${API_BASE}/exports`, { headers: headers() });
  return j<{ exports: any[] }>(res);
}

export async function downloadExport(export_id: string, file: string) {
  const url = `${API_BASE}/exports/download?export_id=${export_id}&file=${encodeURIComponent(file)}`;
  const res = await fetch(url, { headers: headers() });
  if (!res.ok) throw new Error('download failed');
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = file;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function deleteExport(export_id: string) {
  const res = await fetch(`${API_BASE}/exports/${export_id}`, {
    method: 'DELETE',
    headers: headers(),
  });
  return j<any>(res);
}

export async function deleteAllData() {
  const res = await fetch(`${API_BASE}/delete-all`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ confirm: 'DELETE ALL MY DATA' }),
  });
  return j<any>(res);
}

// For "paste existing token" multi-device flow
export function restoreToken(t: string) {
  setToken(t);
}
